import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "gatsby";
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  Dialog,
  DialogContent,
  IconButton,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import Logo from "/public/assets/A-logo-primary.svg";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      margin: theme.spacing(2),
      maxWidth: "800px",
      maxHeight: "90vh",
      backgroundColor: "transparent",
      boxShadow: "none",
      [theme.breakpoints.down("sm")]: {
        margin: 0,
        maxHeight: "100vh",
        maxWidth: "100vw",
        width: "100%",
        height: "100%",
      },
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  },
  dialogContent: {
    padding: 0,
    overflowX: "hidden",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE and Edge
  },
  root: {
    backgroundColor: theme.palette.background.default, // Clean white background
    borderLeft: `6px solid ${theme.palette.primary.main}`,
    position: "relative",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    zIndex: 1,
    color: theme.palette.text.primary,
  },
  container: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(6),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
      paddingTop: theme.spacing(5),
    },
  },
  iconBox: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  icon: {
    width: 48,
    height: 48,
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },
  title: {
    fontWeight: 500,
    fontSize: "1.75rem",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    [theme.breakpoints.up("md")]: {
      fontSize: "2.25rem",
    },
  },
  subtitle: {
    fontWeight: 700,
    fontSize: "1.25rem",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.up("md")]: {
      fontSize: "1.5rem",
    },
  },
  paragraph: {
    marginBottom: theme.spacing(2.5),
    lineHeight: 1.8,
    fontSize: "1.1rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    [theme.breakpoints.up("md")]: {
      fontSize: "1.2rem",
    },
  },
  signed: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontSize: "1.1rem",
    fontWeight: 500,
    fontStyle: "italic",
    color: theme.palette.text.primary,
    textAlign: "right",
    [theme.breakpoints.up("md")]: {
      fontSize: "1.2rem",
    },
  },
  quote: {
    fontStyle: "italic",
    fontSize: "1.1rem",
    fontWeight: 500,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    color: theme.palette.text.primary,
    [theme.breakpoints.up("md")]: {
      fontSize: "1.2rem",
    },
  },
  buttonContainer: {
    marginTop: theme.spacing(4),
    display: "flex",
    justifyContent: "center",
  },
  button: {
    fontWeight: 700,
    fontSize: "1rem",
    padding: theme.spacing(2, 5),
  },
}));

const ClosingAnnouncement = ({ announcement }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (announcement) {
      // Check if user has dismissed the modal before
      const dismissed = localStorage.getItem("closingAnnouncementDismissed");
      if (!dismissed) {
        // Small delay to ensure page is loaded
        setTimeout(() => setOpen(true), 300);
      }

      // Listen for custom event to open modal
      const handleOpenModal = () => {
        setOpen(true);
      };

      window.addEventListener("openClosingAnnouncement", handleOpenModal);

      return () => {
        window.removeEventListener("openClosingAnnouncement", handleOpenModal);
      };
    }
  }, [announcement]);

  const handleClose = () => {
    setOpen(false);
    // Remember that user dismissed it
    localStorage.setItem("closingAnnouncementDismissed", "true");
  };

  if (!announcement) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogContent className={classes.dialogContent}>
        <Paper elevation={3} className={classes.root}>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Container maxWidth="md" className={classes.container}>
            <Box className={classes.iconBox}>
              <Logo className={classes.icon} height={70} fill={theme.palette.primary.main} />
              <Typography variant="h3" component="h1" className={classes.title}>
                {announcement.title}
              </Typography>
            </Box>
     <Typography variant="h6" component="p" className={classes.quote}>
              {announcement.quote}
            </Typography>
            <Typography variant="h6" component="p" className={classes.paragraph}>
              {announcement.mainMessage}
            </Typography>

            <Typography variant="h6" component="p" className={classes.paragraph}>
              {announcement.mentorsThanks}
            </Typography>
           


            <Typography variant="h6" component="p" className={classes.paragraph}>
              {announcement.vision}
            </Typography>

            <Typography variant="h6" component="p" className={classes.paragraph}>
              {announcement.achievement}
            </Typography>


       
            <Typography variant="h6" component="p" className={classes.paragraph}>
              {announcement.closing}
            </Typography>

            {announcement.signed && (
              <Typography variant="h6" component="p" className={classes.signed}>
                — {announcement.signed}
              </Typography>
            )}
          </Container>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

ClosingAnnouncement.propTypes = {
  announcement: PropTypes.shape({
    title: PropTypes.string,
    greeting: PropTypes.string,
    mainMessage: PropTypes.string,
    vision: PropTypes.string,
    achievement: PropTypes.string,
    mentorsThanks: PropTypes.string,
    quote: PropTypes.string,
    closing: PropTypes.string,
    signed: PropTypes.string,
    furnitureSection: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      buttonText: PropTypes.string,
    }),
  }),
};

export default ClosingAnnouncement;

